// Caché client-side de llamadas GIS que van directas desde el frontend (sin pasar por
// nuestro backend, p.ej. getbbox) cuando gis_version="legacy" (ver Settings.js /
// isGisV2). En v2 esas mismas llamadas van siempre proxiadas por el backend, que ya las
// cachea en Redis (ver GisProxyClient en timac_django) — este módulo solo se usa desde
// las ramas legacy de Gis.js, nunca desde las de v2.
//
// Usa localStorage (persiste aunque se recargue o cierre la app) para el valor
// cacheado, y un Map en memoria para deduplicar peticiones en vuelo (no necesita
// persistir: si se recarga la app a media petición, se pierde igualmente el fetch).

const STORAGE_PREFIX = 'gis_front_cache:';

const inFlightRequests = new Map();

function storageKey(key) {
  return `${STORAGE_PREFIX}${key}`;
}

function get(key) {
  let raw;
  try {
    raw = window.localStorage.getItem(storageKey(key));
  } catch (error) {
    return null; // localStorage no disponible (modo privado, etc.): sin caché, no roto.
  }
  if (!raw) return null;

  let entry;
  try {
    entry = JSON.parse(raw);
  } catch (error) {
    window.localStorage.removeItem(storageKey(key));
    return null;
  }

  if (!entry || typeof entry.expiresAt !== 'number' || Date.now() >= entry.expiresAt) {
    window.localStorage.removeItem(storageKey(key));
    return null;
  }

  return entry.value;
}

function set(key, value, ttlMs) {
  try {
    window.localStorage.setItem(
      storageKey(key),
      JSON.stringify({ value, expiresAt: Date.now() + ttlMs }),
    );
  } catch (error) {
    // Best-effort: si localStorage falla (cuota llena, modo privado...), seguimos sin
    // caché en vez de romper la llamada que sí obtuvo el dato real.
  }
}

/**
 * Devuelve el valor cacheado para `key` si sigue vigente; si no, lo obtiene con
 * `fetcher()` y lo cachea con `ttlMs`. Si ya hay una petición en curso para la misma
 * `key` (p.ej. el warmup disparado al pulsar la lupa), se engancha a esa misma
 * promesa en vez de duplicar la llamada.
 *
 * @param {string} key
 * @param {number} ttlMs
 * @param {() => Promise<any>} fetcher
 * @returns {Promise<any>}
 */
function getOrFetch(key, ttlMs, fetcher) {
  const cached = get(key);
  if (cached !== null) {
    return Promise.resolve(cached);
  }

  if (inFlightRequests.has(key)) {
    return inFlightRequests.get(key);
  }

  const promise = fetcher()
    .then((result) => {
      set(key, result, ttlMs);
      return result;
    })
    .finally(() => {
      inFlightRequests.delete(key);
    });

  inFlightRequests.set(key, promise);
  return promise;
}

export default {
  get,
  set,
  getOrFetch,
};
